import { createContext, type PropsWithChildren, useContext, useMemo } from "react";
import type { KeycloakContext } from "@keycloak/keycloak-ui-shared";
import { type AccountEnvironment, KeycloakProvider, useEnvironment } from "@keycloak/keycloak-account-ui";
import { environment } from "./environment";
import { DonorService } from "./donor-service";
import { getAccessToken } from "./api/token.ts";

export type KeycloakContextWithDonorService = KeycloakContext<AccountEnvironment> & {
    donorService: DonorService
}

export function useEnvironmentWithDonorService (): KeycloakContextWithDonorService {
    const { keycloak, environment } = useEnvironment<AccountEnvironment>();
    return {
        donorService: useDonorService(),
        environment,
        keycloak,
    };
}

export function KeycloakProviderWithDonorService ({ children }: PropsWithChildren) {
    return (
        <KeycloakProvider environment={environment}>
            <DonorServiceProvider>
                {children}
            </DonorServiceProvider>
        </KeycloakProvider>
    );
}

const DonorServiceContext = createContext<DonorService | null>(null);

function useDonorService (): DonorService {
    const context = useContext(DonorServiceContext);
    if (!context) {
        throw new Error("Donor service not initialized, make sure to add the provider");
    }
    return context;
}

function DonorServiceProvider ({ children }: PropsWithChildren) {
    const { keycloak } = useEnvironment();
    const donorService = useMemo(
        () => new DonorService({ getAccessToken: getAccessToken(keycloak) }),
        [ keycloak ],
    );

    return (
        <DonorServiceContext.Provider value={donorService}>
            {children}
        </DonorServiceContext.Provider>
    );
}
